// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Billing / Subscription API Hooks
// apps/web/src/lib/api/billing.ts
//
// Two hooks:
//
//   useSubscription()           — GET  /api/v1/billing/subscription
//   useCreateCheckoutSession()  — POST /api/v1/billing/create-checkout-session
//
// Both follow the shared useApiClient pattern so auth + base URL handling
// stays consistent across the app.
//
// 2026.4.9: `tier` is now a proper union type aligned with the server's
// PlanMatrix (`Free | Builder | Professional | Enterprise`). The old comment
// listed phantom tiers (`Starter`, `Pro`, `Business`) that the server never
// actually returns — that was a documentation-only bug. The `normalizeTier`
// helper handles legacy DB rows that still have the old `"Starter"` value
// from before the PlanMatrix fix, so existing dev tenants don't break.
//
// 2026.10.1: Billing on ForgeMint. `embeddedBilling` on the subscription says that the
// owner's billing is shown on /billing by ForgeMint's billing frame (@forgemint/embed), and
// useBillingClientSecret() asks the API for that frame's component session.
//
// 2026.10.2: With billing on ForgeMint, an upgrade's checkout shows on /billing too:
// useEmbeddedCheckoutClientSecret() asks the API for the checkout session's client secret
// (POST /api/v1/billing/embedded-checkout-session), which ForgeMint's checkout frame shows.
//
// 2026.4.9: New-tenant signup no longer touches this endpoint. The upgrade
// flow (existing tenant → new plan) is now the ONLY caller of
// /billing/create-checkout-session, and the DTO is just { planId, cycle }.
// See BillingController.cs for the security rationale.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type { PlanId, BillingCycle } from '../../pages/signup/plans';

// ─── Subscription tier ──────────────────────────────────────────────────────

/** Canonical subscription tier names as stored in Tenant.SubscriptionTier. */
export type SubscriptionTier = 'Free' | 'Builder' | 'Professional' | 'Enterprise';

/**
 * Normalizes a raw tier string from the API into a canonical SubscriptionTier.
 * Handles the legacy `"Starter"` value from before the PlanMatrix fix (see
 * TenantProvisioningService.cs). Unknown values fall back to `"Free"` so the
 * UI renders something sensible rather than crashing.
 */
export function normalizeTier(raw: string | null | undefined): SubscriptionTier {
  switch ((raw ?? '').trim().toLowerCase()) {
    case 'builder':
    case 'starter':        // legacy: pre-2026.4.9 PlanMatrix value
      return 'Builder';
    case 'professional':
    case 'pro':            // legacy
      return 'Professional';
    case 'enterprise':
    case 'business':       // legacy (never actually shipped, but defensive)
      return 'Enterprise';
    case 'free':
    default:
      return 'Free';
  }
}

// ─── Tier rank helpers ─────────────────────────────────────────────────────

const TIER_RANK: Record<SubscriptionTier, number> = {
  Free: 0,
  Builder: 1,
  Professional: 2,
  Enterprise: 3,
};

/** Returns true if `current` tier meets or exceeds `required` tier. */
export function meetsMinTier(current: SubscriptionTier, required: SubscriptionTier): boolean {
  return TIER_RANK[current] >= TIER_RANK[required];
}

/** Maps a SubscriptionTier back to its corresponding PlanId for the picker. */
export function tierToPlanId(tier: SubscriptionTier): PlanId {
  switch (tier) {
    case 'Builder':      return 'builder';
    case 'Professional': return 'professional';
    case 'Enterprise':   return 'enterprise';
    case 'Free':         return 'free';
  }
}

// ─── GET /api/v1/billing/subscription ──────────────────────────────────────

export interface SubscriptionInfo {
  // ── Tenant context (server still returns these; no longer used by the
  //    frontend for checkout — the API reads tenant from the JWT now — but
  //    kept for display in the badge / settings page)
  tenantId:           number;
  subdomain:          string;
  billingEmail:       string | null;
  // ── Subscription state ──────────────────────────────────────────────
  /** Raw tier string from the server. Use `normalizeTier()` before rendering. */
  tier:               string;
  status:             string;   // "Active" | "Trialing" | "PastDue" | "Canceled" | ...
  stripeConnected:    boolean;
  currentPeriodEnd?:  string;
  cancelAtPeriodEnd?: boolean;
  stripeError?:       boolean;
  /** Billing is on ForgeMint, and this is the owner: ForgeMint's billing frame shows it on /billing. */
  embeddedBilling?:   boolean;
}

interface UseSubscriptionState {
  data:    SubscriptionInfo | null;
  loading: boolean;
  error:   Error | null;
  refetch: () => Promise<void>;
}

export function useSubscription(enabled = true): UseSubscriptionState {
  const { createApi } = useApiClient();
  const [data,    setData]    = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error,   setError]   = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const api      = await createApi();
      const response = await api.get<SubscriptionInfo>('/api/v1/billing/subscription');
      setData(response.data);
    } catch (err: any) {
      // 403 (non-Owner/Admin) is expected for most users — fall back to
      // showing nothing rather than an error. The badge component checks
      // for null data and renders nothing in that case.
      if (err.response?.status === 403) {
        setData(null);
      } else {
        setError(new Error(err.response?.data?.message || err.message || 'Request failed'));
      }
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => {
    if (enabled) fetch();
  }, [fetch, enabled]);

  return { data, loading, error, refetch: fetch };
}

// ─── POST /api/v1/billing/component-session ────────────────────────────────

/**
 * Asks the API for a ForgeMint component session's client secret for the
 * tenant's billing (Owner-only). ForgeMint's billing frame calls it when it
 * opens, and again each time its session ends.
 */
export function useBillingClientSecret() {
  const { createApi } = useApiClient();

  return useCallback(async (): Promise<string> => {
    try {
      const api = await createApi();
      const response = await api.post<{ clientSecret: string }>('/api/v1/billing/component-session');
      return response.data.clientSecret;
    } catch (err: any) {
      throw new Error(err?.response?.data?.message || err?.message || 'Billing could not be opened.');
    }
  }, [createApi]);
}

// ─── POST /api/v1/billing/embedded-checkout-session ────────────────────────

/**
 * With billing on ForgeMint, an upgrade's checkout in ForgeTrack's own Billing page (ForgeMint's
 * embedded checkout): the checkout session's client secret, for ForgeMint's checkout frame. Each
 * call makes a new checkout session. Owner-only, as the hosted checkout is.
 */
export function useEmbeddedCheckoutClientSecret() {
  const { createApi } = useApiClient();

  return useCallback(
    async (planId: Exclude<PlanId, 'free'>, billingCycle: BillingCycle): Promise<string> => {
      try {
        const api = await createApi();
        const response = await api.post<{ clientSecret: string }>(
          '/api/v1/billing/embedded-checkout-session',
          { planId, billingCycle },
        );
        return response.data.clientSecret;
      } catch (err: any) {
        throw new Error(
          err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Checkout could not be opened.',
        );
      }
    },
    [createApi],
  );
}

// ─── POST /api/v1/billing/create-checkout-session ──────────────────────────

export interface CreateCheckoutSessionResult {
  checkoutUrl: string;
  sessionId:   string;
}

/**
 * Opens a Stripe Checkout session for an UPGRADE (existing tenant changing
 * plans). Owner-only — the server enforces this from the JWT, so callers
 * don't need to check.
 *
 * Free is not a valid upgrade target (the server rejects it with 400). The
 * caller should handle free plans via a different downgrade flow.
 *
 * For the initial new-tenant signup flow, do NOT use this hook — use
 * POST /api/v1/signup/create-tenant instead, which creates the tenant AND
 * the Stripe session in one call.
 */
export function useCreateCheckoutSession() {
  const { createApi } = useApiClient();

  return useCallback(
    async (planId: Exclude<PlanId, 'free'>, billingCycle: BillingCycle): Promise<CreateCheckoutSessionResult> => {
      const api = await createApi();
      const response = await api.post<CreateCheckoutSessionResult>(
        '/api/v1/billing/create-checkout-session',
        { planId, billingCycle },
      );
      return response.data;
    },
    [createApi],
  );
}
